import {
    Button, ButtonGroup, Dialog, DialogBody, DialogContainer, DialogFooter, DialogHeader
} from '@wg/wows-react-uikit';
import classnames from 'classnames';
import equal from 'fast-deep-equal/react';
import get from 'lodash/get';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash/cloneDeep';
import styles from './RewardsDialog.scss';
import type { GameItemReward, IReward } from '~/types';
import { useAppSelector } from '~/hooks';
import HeaderSubtitle from '~/Components/HeaderSubtitle/HeaderSubtitle';
import RewardCompensation from '~/Components/RewardCompensation/RewardCompensation';
import RewardShip from '~/Components/RewardShip/RewardShip';
import WidthWrapper from '~/Components/WidthWrapper/WidthWrapper';
import { RootState as State } from '~/store';
import { isInIframe } from '~/utils';
import UnitedReward from '~/Components/UnitedReward/UnitedReward';
import { Button as DockyardButton } from '~/Components/Buttons/Buttons';

export interface RewardsDialog_Props {
    isVisible: boolean,
    ids: Array<string>,
    title: string | JSX.Element,
    subtitle?: string,
    actionButtonText: string,
    isBuyAction?: boolean,
    isActionDisabled?: boolean,
    footerContent?: React.ReactNode,

    onHide: () => void,
    onActionButtonPress: () => void,
}

export interface RewardsDialogState {
    rewards: Array<IReward>,
    eventum: number,
    compensations: Array<CompensationItem>,
}

const stateSelector = (state: State): RewardsDialogState => {
    const eventum = get(state.app, 'account.wallet.eventum', 0) as number;

    return {
        rewards: state.app.rewards,
        eventum,
        compensations: state.app.compensations,
    };
};

const RewardsDialog = (props: RewardsDialog_Props) => {
    const { rewards, eventum, compensations } = useAppSelector(stateSelector, equal);
    const {
        isVisible,
        ids,
        title,
        subtitle,
        actionButtonText,
        isActionDisabled,
        isBuyAction,
        footerContent,

        onActionButtonPress,
        onHide,
    } = props;

    const { t } = useTranslation();
    const isFrame = isInIframe();

    const combineRewards = (rewards: Array<IReward>) => {
        const allRewards: GameItemReward[] = [];
        const combinedRewards: GameItemReward[] = [];
        rewards?.forEach((reward: IReward) => {
            reward.gameRewards.forEach((gameReward: GameItemReward) => {
                allRewards.push(gameReward);
            });
        });
        allRewards?.forEach((gameReward: GameItemReward) => {
            let added = false;
            for (let i = 0; i < combinedRewards.length; i++) {
                if (combinedRewards[i].id === gameReward.id && combinedRewards[i].type === gameReward.type) {
                    combinedRewards[i].amount = (combinedRewards[i].amount || 0) + (gameReward.amount || 0);
                    added = true;
                    break;
                }
            }
            if (!added) {
                combinedRewards.push(cloneDeep(gameReward));
            }
        });

        return combinedRewards;
    };

    const renderRewards = (isShip = false) => {
        const compensationMultiplier = eventum;
        const icons: Array<React.ReactNode> = [];

        if (compensationMultiplier > 0 && !isShip) {
            compensations.forEach((compensationItem) => {
                icons.push(
                    <div className={styles.rewardWrapper} key={`reward-${compensationItem.type}-${compensationItem.amount}`}>
                        <RewardCompensation
                            key={`compensation-reward-${compensationItem.type}`}
                            {...compensationItem}
                            multiplier={compensationMultiplier}
                        />
                    </div>,
                );
            });
        }

        const filteredRewards = rewards.filter((reward) => {
            let isShipReward = false;
            const found = false;
            reward.gameRewards.forEach((gameReward) => {
                if (found === false && gameReward.type === 'ship') {
                    isShipReward = true;
                }
            });

            return isShip === isShipReward
                && ids.includes(reward.id)
                && reward.gameRewards
                && reward.gameRewards.length > 0;
        });

        if (filteredRewards.length < 1) {
            return false;
        }

        if (isShip) {
            filteredRewards.forEach((reward: IReward) => {
                const shipId = reward.gameRewards[0].id;
                icons.push(<RewardShip id={shipId as string} fixedTooltip={false} reward={reward} isBig key={`RewardShip-${shipId}`} />);
            });
            return icons;
        }
        const unitedRewards = combineRewards(filteredRewards);
        unitedRewards.forEach((reward: IReward, index: number) => {
            icons.push(<UnitedReward rewards={filteredRewards} {...reward} hideText={true} key={`UnitedReward-${index}`} />);
        });

        return icons;
    };

    const renderContent = () => {
        return (
            <div className={styles.contentWrapper}>
                <div className={styles.iconsRow}>{renderRewards(true)}</div>
                <div className={styles.iconsRow}>{renderRewards()}</div>
            </div>
        );
    };

    const renderDialog = () => {
        return (
            <DialogContainer>
                <DialogHeader>
                    {isFrame && (
                        <div className={styles.armoryTitle} onClick={onHide}>
                            {t('Стартовые наборы')}
                        </div>
                    )}
                    {title}
                </DialogHeader>
                <DialogBody>
                    {subtitle && <HeaderSubtitle>{subtitle}</HeaderSubtitle>}
                    <WidthWrapper>{renderContent()}</WidthWrapper>
                </DialogBody>
                <DialogFooter>
                    {!!footerContent && <div className={styles.footerContent}>{footerContent}</div>}

                    {isBuyAction ? (
                        <>
                            <ButtonGroup>
                                <Button isFlat={true} type={'orange'} onClick={onActionButtonPress} isDisabled={isActionDisabled}>
                                    {t('Да')}
                                </Button>
                                <DockyardButton label={t('Нет')} onClick={onHide} />
                            </ButtonGroup>
                        </>
                    ) : (
                        <ButtonGroup>
                            <Button isFlat={true} onClick={onActionButtonPress} isDisabled={isActionDisabled}>
                                {actionButtonText}
                            </Button>
                        </ButtonGroup>
                    )}
                </DialogFooter>
            </DialogContainer>
        );
    };

    const dialog = classnames(styles.dialog, {
        [styles.dialogArmory]: isFrame,
    });

    return <Dialog className={dialog} content={renderDialog()} size={'worship'} isOverlay={false} isVisible={isVisible} hideDialog={onHide} />;
};

export default memo(RewardsDialog);
